import request from '@flowio/request';
import session from '@flowio/session';
import { appendQueryParameters } from '@flowio/web-sdk';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import some from 'lodash/some';
import get from 'lodash/get';
import inRange from 'lodash/inRange';
import isString from 'lodash/isString';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import pickBy from 'lodash/pickBy';
import toArray from 'lodash/toArray';
import uniq from 'lodash/uniq';
import constants from './constants';

const ItemAvailability = {
  InStock: 'http://schema.org/InStock',
  /** Indicates that the item is available only at physical locations. */
  InStoreOnly: 'http://schema.org/InStoreOnly',
  /** Indicates that the item has limited availability. */
  LimitedAvailability: 'http://schema.org/LimitedAvailability',
  /** Indicates that the item is available only online. */
  OnlineOnly: 'http://schema.org/OnlineOnly',
  /** Indicates that the item is out of stock. */
  OutOfStock: 'http://schema.org/OutOfStock',
  /** Indicates that the item is available for pre-order,
   * but will be delivered when generally available. */
  PreOrder: 'http://schema.org/PreOrder',
  /** Indicates that the item is available for ordering and delivery before general availability. */
  PreSale: 'http://schema.org/PreSale',
  /** Indicates that the item has sold out. */
  SoldOut: 'http://schema.org/SoldOut',
};

class SchemaOrgGenerator {
  constructor() {
    this.schemaGenerated = false;
  }

  static fetchItemsByQuery(
    {
      organization, experienceKey, currency, itemNumbers,
    },
    callback = noop,
  ) {
    return new Promise((resolve, reject) => {
      const url = `${constants.FLOW_API_HOST}/${organization}/experiences/${experienceKey}/items/query`;
      const params = {
        currency,
        q: `number in (${itemNumbers.join(',')})`,
      };
      const urlWithParms = appendQueryParameters(url, params);
      request.anonymous(
        {
          url: urlWithParms,
          method: 'GET',
        },
        (status, response) => {
          callback(status, response);
          if (inRange(status, 200, 300)) {
            resolve(response);
          } else {
            reject(response);
          }
        },
      );
    });
  }


  static getSchemaOffer(item) {
    // TODO: localized_item_price instead of 0
    // TODO: availability from server

    const localizedItemPrice = find(get(item, 'local.prices'), { key: 'localized_item_price' });
    const offerAvailability = get(item, 'attributes.availability');
    let availability;

    if (offerAvailability) {
      availability = offerAvailability;
    } else {
      // temp setting prices to be in stock when price = 0
      availability = get(localizedItemPrice, 'amount') === 0
        ? ItemAvailability.InStock
        : ItemAvailability.InStock;
    }

    const schemaOffer =
      pickBy(
        {
          '@context': 'http://schema.org/',
          '@id': item.number,
          availability,
          '@type': 'Offer',
          'price': get(localizedItemPrice, 'amount'),
          'priceCurrency': get(localizedItemPrice, 'currency'),
        },
      );

    return schemaOffer;
  }

  // Rescursive function to scrape @id properties from existing client schema
  static getSchemaIds(obj) {
    return flatten(Object.keys(obj)
      .map((key) => {
        const value = obj[key];
        if (key === '@id') {
          return value;
        } else if (typeof value === 'object') {
          return SchemaOrgGenerator.getSchemaIds(value);
        } return [];
      },
      ));
  }

  static schemaHasProduct(schema) {
    return some(Object.keys(schema), key => key === '@type' && schema[key] === 'Product');
  }

  static setSchema(json) {
    const el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('id', 'flowjs-schema-org');
    el.innerHTML = JSON.stringify(json);

    document.head.appendChild(el);
  }

  static getAvailability(schemaOffers) {
    return map(schemaOffers, offer => ({
      stock: offer.availability,
      itemNumber: offer['@id'],
    }),
    );
  }

  selectElements() {
    this.itemElements = toArray(document.querySelectorAll(`[${constants.ITEM_FLOW_VARIANT}]`));
    return this.itemElements;
  }


  static getItemElementsIds(items) {
    return filter(uniq(map(items, item => item.getAttribute(constants.ITEM_FLOW_VARIANT))),
      isString);
  }

  static getItemIds(items) {
    return uniq(map(items, item => item.id),
      isString);
  }

  static schemaAggregateOffer(schema, offers) {
    if (offers.length) {
      return {
        ...schema,
        offers: merge(schema.offers, offers),
      };
    }
    return schema;
  }

  static setMainSchema(schema, offers) {
    const aggregateOffer = SchemaOrgGenerator.schemaAggregateOffer(schema, offers);
    SchemaOrgGenerator.setSchema(aggregateOffer);
  }

  generateSchemaOrgJson() {
    return new Promise((resolve) => {
      const existingSchemaElements = document.querySelectorAll(constants.CLIENT_SCHEMA);
      const sessionHasExperience = session.getExperience();
      const noExistingFlowSchemaMessage = 'No existing vnd.flow.ld+json schema on page. Flow localized schema will not be created.';
      if (sessionHasExperience) {
        const existingJSONLDSchema = document.querySelectorAll(constants.LD_JSON_SCHEMA);
        if (existingJSONLDSchema.length) {
          const regularSchemaMessage = 'Regular schema has been detected so Flow will not attempt to generate a localized schema.';
          console.info(regularSchemaMessage);
          return resolve(regularSchemaMessage);
        }
        const parsedSchemas = [];
        if (existingSchemaElements.length === 0) {
          console.info(noExistingFlowSchemaMessage);
          return resolve(noExistingFlowSchemaMessage);
        }
        const schemaElements = filter(existingSchemaElements, (schemeEle) => {
          try {
            const sanitizedJson = schemeEle.innerHTML.replace(/[\n\t]/g, ' ').replace(/\s+/, ' ');
            const parsedSchema = JSON.parse(sanitizedJson);

            if (SchemaOrgGenerator.schemaHasProduct(parsedSchema)) {
              // remove Flow vendor schema from DOM as we already have its contents parsed
              schemeEle.parentNode.removeChild(schemeEle);
              parsedSchemas.push(parsedSchema);
              return true;
            }
          } catch (e) {
            console.log(e);
          }

          schemeEle.setAttribute('type', 'application/ld+json');
          return false;
        });


        const promises = map(schemaElements, (schemaElement, i) => {
          const parsedSchema = parsedSchemas[i];
          const schemaIds = SchemaOrgGenerator.getSchemaIds(parsedSchema);

          if (schemaIds.length && sessionHasExperience) {
            // Get offers and populate schema
            return SchemaOrgGenerator.fetchItemsByQuery({
              organization: session.getOrganization(),
              experienceKey: session.getExperience(),
              currency: session.getCurrency(),
              itemNumbers: schemaIds,
            }).then((res) => {
              const availabilityNumbers = SchemaOrgGenerator.getAvailability(parsedSchema.offers);

              const responseItems = map(get(res, 'items', []), (item) => {
                // ld_match items to get availability
                const matchedItem = find(availabilityNumbers, { itemNumber: item.number });

                return matchedItem ? {
                  ...item,
                  attributes: {
                    ...item.attributes,
                    availability: matchedItem.stock,
                  },
                } : item;
              });

              const offers = responseItems.map(SchemaOrgGenerator.getSchemaOffer);
              SchemaOrgGenerator.setMainSchema(parsedSchema, offers);
              this.schemaGenerated = true;
              return resolve();
            });
          }

          SchemaOrgGenerator.setMainSchema(parsedSchema, []);
          return resolve();
        });

        return Promise.all(promises);
      }

      // if no session detected

      if (existingSchemaElements.length) {
        forEach(existingSchemaElements, (node) => {
          node.setAttribute('type', 'application/ld+json');
        });
      }

      return resolve();
    }).catch(e => Promise.reject(e));
  }
}

export default SchemaOrgGenerator;



// WEBPACK FOOTER //
// ./static/js/v0/schema-org.js